import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  IconArrowLeft,
  IconSend,
  IconLoader2,
  IconHeadset,
} from "@tabler/icons-react";
import { toast } from "sonner";
import { getTicket, replyTicket } from "../api";
import { STATUS_TONE, PRIORITY_TONE, formatDateTime } from "../constants";
import { cn } from "@/lib/utils/cn";

export function TicketDetailPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState("");

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["agency-support-ticket", id],
    queryFn: () => getTicket(id),
    enabled: !!id,
  });

  const replyMutation = useMutation({
    mutationFn: (body: string) => replyTicket(id, body),
    onSuccess: () => {
      setReply("");
      queryClient.invalidateQueries({ queryKey: ["agency-support-ticket", id] });
      queryClient.invalidateQueries({ queryKey: ["agency-support-tickets"] });
    },
    onError: () => toast.error(t("support.detail.replyError")),
  });

  const isClosed = ticket?.status === "closed" || ticket?.status === "resolved";

  const onSend = () => {
    const body = reply.trim();
    if (!body) return;
    replyMutation.mutate(body);
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center text-ink-soft font-bold italic animate-pulse">
        {t("support.detail.loading")}
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="space-y-4">
        <Link
          to="/support"
          className="inline-flex items-center gap-2 text-sm font-bold text-brand-600 hover:underline"
        >
          <IconArrowLeft size={16} /> {t("support.detail.back")}
        </Link>
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-red-600">
          <h3 className="font-bold">{t("support.detail.notFound")}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/support"
        className="inline-flex items-center gap-2 text-sm font-bold text-brand-600 hover:underline"
      >
        <IconArrowLeft size={16} /> Voltar para chamados
      </Link>

      {/* HEADER */}
      <div className="rounded-3xl border border-white/40 bg-white/70 p-8 shadow-card backdrop-blur-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="space-y-1">
            <span className="text-[11px] font-black uppercase tracking-widest text-ink-soft">
              {ticket.code}
            </span>
            <h2 className="text-2xl font-black tracking-tight text-ink">
              {ticket.subject}
            </h2>
            <p className="text-sm text-ink-muted">
              {t("support.openedAt", {
                date: formatDateTime(ticket.createdAt),
              })}
              {ticket.channel === "portal" && ` · ${t("support.viaPortal")}`}
              {ticket.requesterName && ` · ${ticket.requesterName}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wider",
                STATUS_TONE[ticket.status],
              )}
            >
              {t(`support.status.${ticket.status}`)}
            </span>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-wider",
                PRIORITY_TONE[ticket.priority],
              )}
            >
              {t(`support.priority.${ticket.priority}`)}
            </span>
            <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-ink-soft">
              {t(`support.category.${ticket.category}`)}
            </span>
          </div>
        </div>
      </div>

      {/* THREAD */}
      <div className="rounded-3xl border border-white/40 bg-white/70 shadow-card backdrop-blur-sm overflow-hidden">
        <div className="border-b border-border-soft bg-gray-50/30 px-8 py-5">
          <h3 className="font-black text-ink">
            {t("support.detail.conversation")}
          </h3>
        </div>
        <div className="space-y-5 p-8">
          {ticket.messages.map((m) => {
            const isAgent = m.authorRole === "agent";
            return (
              <div
                key={m.id}
                className={cn("flex gap-3", isAgent ? "flex-row" : "flex-row-reverse")}
              >
                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-xs font-black shadow-inner-light",
                    isAgent
                      ? "bg-brand-grad text-white"
                      : "bg-gray-100 text-ink-soft",
                  )}
                >
                  {isAgent ? <IconHeadset size={16} /> : m.author.substring(0, 2).toUpperCase()}
                </div>
                <div
                  className={cn(
                    "max-w-[78%] space-y-1",
                    isAgent ? "items-start" : "items-end",
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-ink-soft",
                      isAgent ? "justify-start" : "justify-end",
                    )}
                  >
                    <span>{m.author}</span>
                    <span className="opacity-60">{formatDateTime(m.at)}</span>
                  </div>
                  <div
                    className={cn(
                      "rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-soft",
                      isAgent
                        ? "rounded-tl-sm bg-brand-50 text-ink"
                        : "rounded-tr-sm bg-white text-ink border border-border-soft",
                    )}
                  >
                    {m.body}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* REPLY */}
        <div className="border-t border-border-soft bg-gray-50/30 p-6">
          {isClosed ? (
            <p className="text-center text-sm font-bold text-ink-soft italic">
              {t("support.detail.closedNotice", {
                status: t(`support.status.${ticket.status}`).toLowerCase(),
              })}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                placeholder={t("support.detail.replyPlaceholder")}
                className="w-full resize-none rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none transition-all focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10"
              />
              <div className="flex justify-end">
                <button
                  onClick={onSend}
                  disabled={replyMutation.isPending || !reply.trim()}
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-grad px-6 py-2.5 text-sm font-black uppercase tracking-wider text-white shadow-soft transition-all hover:scale-105 hover:shadow-glow active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
                >
                  {replyMutation.isPending ? (
                    <IconLoader2 size={18} className="animate-spin" />
                  ) : (
                    <IconSend size={18} />
                  )}
                  {t("support.detail.reply")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
